export const GITOPS_REPO_DEFAULT = 'hulkag/internal-apps-gitops';
export const NEW_REPO_TEMPLATE_DEFAULT = 'ds24-labs/app-blueprint';

/**
 * Why a GitHub token cannot act on a target, and what fixes it.
 *
 * - `sso`: the organization enforces SAML SSO and this token was authorized
 *   without an active SSO session for it. GitHub says so with the
 *   `X-GitHub-SSO: required; url=…` header; the URL authorizes exactly this
 *   token for the org (GitHub docs: "Authenticating to the REST API").
 * - `scope`: the token is SSO-authorized but lacks `repo` (a read:user
 *   sign-in from the Settings page). Dropping the GitHub session and
 *   authorizing again requests the scope explicitly.
 * - `org-auth`: the token holds `repo`, but the organization never authorized
 *   it and GitHub said so only by hiding the repository. An OAuth token is
 *   authorized for a SAML organization *at authorization time*, and only if
 *   the user had an active SSO session for it then (GitHub docs: "Authorizing
 *   OAuth apps") — so the fix is to establish that session at `authorizeUrl`
 *   and then mint a *new* token. Retrying the same one can never work, which
 *   is why this is its own kind: callers must re-authorize, not re-probe.
 * - `install`: the token is a GitHub App user token, which has no scopes at
 *   all — its reach is the installation's permissions intersected with the
 *   user's. So an invisible repository is about the *installation*, and no
 *   amount of re-authorizing by the user will change it.
 * - `credentials`: GitHub rejected the token itself with a 401 ("Bad
 *   credentials") — expired, revoked, or minted against a session that has
 *   since gone. Our GitHub App user tokens expire after 8 hours, so this is
 *   reachable simply by leaving a tab open. Like `org-auth` it cannot be
 *   repaired by re-probing: the only fix is to drop the session and mint a
 *   new token, which is why it is a kind of its own rather than a bare Error.
 */
export type AccessProblem =
  | { kind: 'sso'; authorizeUrl?: string }
  | { kind: 'scope' }
  | { kind: 'org-auth'; authorizeUrl: string }
  | { kind: 'install' }
  | { kind: 'credentials' };

/**
 * Problems that re-probing the same token can never fix, so the caller has to
 * mint a new one instead. `sso` is absent on purpose: there the user fixes
 * things at GitHub's `authorizeUrl` and the existing token then works.
 */
export function needsFreshToken(problem?: AccessProblem): boolean {
  return problem?.kind === 'org-auth' || problem?.kind === 'credentials';
}

/** GitHub's URL to start a SAML session for an organization. */
function orgSsoUrl(org: string): string {
  return `https://github.com/orgs/${org}/sso`;
}

export class RepoAccessError extends Error {
  constructor(
    message: string,
    readonly problem: AccessProblem,
  ) {
    super(message);
    this.name = 'RepoAccessError';
  }
}

/** GitHub answers an unusable token with 401 before it considers the target. */
function assertCredentials(res: Response, target: string): void {
  if (res.status !== 401) return;
  throw new RepoAccessError(
    `Your GitHub connection has expired — reconnect to continue. ` +
      `GitHub rejected the token while checking ${target}.`,
    { kind: 'credentials' },
  );
}

/**
 * What a token turned out to be able to do with one probe target. Access is
 * proven by the time this exists — these are the softer facts a caller may
 * want to warn about rather than block on.
 */
export interface GithubAccess {
  /** `owner/name` for a repository probe, a bare `owner` for an org probe. */
  target: string;
  /**
   * The token can push a branch to the repo. When `false`,
   * `publish:github:pull-request` opens the PR from a fork of the repo in the
   * user's own account instead — octokit-plugin-create-pull-request forks
   * whenever a user token lacks push — which is easy to mistake for success.
   * `undefined` when GitHub did not say (an org-only probe, or a body we
   * could not read).
   */
  canPush?: boolean;
  /**
   * Organization policy: may non-owner members create repositories? GitHub
   * returns `members_can_create_repositories` only to organization owners, so
   * this is `undefined` for everyone else, and callers fall back to
   * configuration rather than guessing.
   */
  membersCanCreateRepos?: boolean;
}

function orgOf(slug: string): string {
  return slug.split('/')[0];
}

function headersFor(token: string) {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/vnd.github+json',
  };
}

export function ssoHint(target: string, hasLink: boolean): string {
  const org = orgOf(target);
  const how = hasLink
    ? `Click "Authorize for ${org}", complete the single sign-on, then retry.`
    : `Grant it once under GitHub → Settings → Applications → Authorized OAuth Apps → Backstage → Organization access → ${org}, then retry.`;
  const symptom =
    target === org
      ? `its repositories look like "Not Found"`
      : `${target} looks like "Not Found"`;
  return (
    `Your GitHub authorization for Backstage is not yet authorized for the ` +
    `${org} organization, which enforces SAML SSO, so ${symptom}. ${how}`
  );
}

export function scopeHint(target: string): string {
  return `Your GitHub sign-in does not include repository access, so Backstage cannot act on ${target} as you.`;
}

/**
 * The token demonstrably holds `repo` and the organization answered, yet the
 * repository is invisible. Not a scope problem — the organization has not
 * authorized this token (SAML single sign-on, or an OAuth App access policy),
 * or the repository is not one this user can see.
 */
export function orgAuthHint(target: string): string {
  const org = orgOf(target);
  const what = target === org ? `its repositories` : target;
  return (
    `${org} has not authorized your GitHub sign-in, so ${what} looks like ` +
    `"Not Found" — your sign-in does have repository access. Click ` +
    `"Authorize for ${org}", complete the single sign-on, then Retry. ` +
    `If that does not help, you may not be a member of ${org}, or the ` +
    `repository may not exist.`
  );
}

/**
 * A GitHub App user token could not see the repository. Scopes do not exist
 * for such a token, so this is the installation's reach, not the user's
 * authorization — the fix is on the app, not in the browser.
 */
export function installHint(target: string): string {
  const org = orgOf(target);
  return (
    `The Backstage GitHub App cannot reach ${target}. Either it is not ` +
    `installed on ${org}, or ${target} is outside the repositories the ` +
    `installation was given, or you have no access to it yourself. ` +
    `Re-authorizing will not help — this is fixed on the app's installation ` +
    `for ${org}.`
  );
}

export function forkHint(target: string): string {
  return (
    `You do not have push access to ${target}, so the pull request will be ` +
    `opened from a fork in your own account rather than from the repository ` +
    `itself. That still works, but branch-based CI and required checks on the ` +
    `target repo may not run on it.`
  );
}

export function repoCreationHint(org: string): string {
  return `Only organization owners can create repositories in ${org}. Ask in #devops-support, then choose "a repository that already exists".`;
}

// `X-GitHub-SSO: required; url=https://github.com/orgs/<org>/sso?authorization_request=…`
function ssoAuthorizeUrl(res: Response): string | undefined {
  const header = res.headers.get('x-github-sso') ?? '';
  const m = /url=(\S+)/.exec(header);
  return header.startsWith('required') ? m?.[1] : undefined;
}

/**
 * A GitHub App user-to-server token (`ghu_`). Such a token has no OAuth
 * scopes at all — its reach is the installation's permissions intersected
 * with the user's — so every scope-shaped conclusion is meaningless for it.
 */
function isAppUserToken(token: string): boolean {
  return token.startsWith('ghu_');
}

function hasRepoScope(res: Response): boolean | undefined {
  // Only OAuth-app tokens report their scopes. Absent header = unknown, and
  // so is an *empty* one: GitHub sends no scope list for tokens that have no
  // scope concept, and reading that as "lacks repo" rejects a perfectly good
  // GitHub App token before its access is ever tested.
  const scopes = res.headers.get('x-oauth-scopes')?.trim();
  if (!scopes) return undefined;
  return scopes
    .split(',')
    .map(s => s.trim())
    .includes('repo');
}

/** Compact "what did GitHub actually say" trail, appended to every access error. */
function step(path: string, res: Response): string {
  const scopes = res.headers.get('x-oauth-scopes');
  const seen =
    scopes === null ? 'no-scope-header' : `scopes=${scopes.trim() || 'empty'}`;
  return `${path} ${res.status} ${seen}`;
}

function withTrace(message: string, trace: string[]): string {
  return `${message} [${trace.join(' · ')}]`;
}

// Reads one boolean out of a JSON body, tolerating a body we cannot parse
// (the probe already succeeded; these are advisory facts, not access).
async function boolField(
  res: Response,
  read: (body: any) => unknown,
): Promise<boolean | undefined> {
  try {
    const value = read(await res.json());
    return typeof value === 'boolean' ? value : undefined;
  } catch {
    return undefined;
  }
}

interface OrgProbe {
  res: Response;
  membersCanCreateRepos?: boolean;
  /** What GitHub said, for the technical trail on any later error. */
  trace: string[];
}

/**
 * Probe `GET /orgs/<org>` — the only endpoint that reports SSO state (and the
 * token's scopes). A repository lookup with a token the org never authorized
 * is a bare 404, which is why this comes first. `target` is what the caller is
 * really after, and names the problem in the error message.
 */
async function probeOrg(
  token: string,
  org: string,
  target: string,
  fetchImpl: typeof fetch,
): Promise<OrgProbe> {
  const res = await fetchImpl(`https://api.github.com/orgs/${org}`, {
    headers: headersFor(token),
  });
  const trace = [step(`orgs/${org}`, res)];
  // Before anything target-specific: a 401 is about the token, not the org,
  // and every later branch would misread it as a fact about access.
  assertCredentials(res, target);
  const sso = ssoAuthorizeUrl(res);
  if (sso || (!res.ok && res.headers.get('x-github-sso'))) {
    throw new RepoAccessError(withTrace(ssoHint(target, Boolean(sso)), trace), {
      kind: 'sso',
      authorizeUrl: sso,
    });
  }
  if (!res.ok && res.status !== 404 && res.status !== 403) {
    throw new Error(`GitHub returned ${res.status} for ${org}`);
  }
  // Scopes say nothing about a GitHub App token, so never conclude from them.
  if (res.ok && !isAppUserToken(token) && hasRepoScope(res) === false) {
    throw new RepoAccessError(withTrace(scopeHint(target), trace), {
      kind: 'scope',
    });
  }
  return {
    res,
    trace,
    membersCanCreateRepos: res.ok
      ? await boolField(res, b => b?.members_can_create_repositories)
      : undefined,
  };
}

/**
 * Prove `token` can open a pull request on `slug`. Resolves with what else the
 * token turned out to be able to do there; throws RepoAccessError describing
 * the fix, or a plain Error for anything that is not about this token's access
 * (GitHub down, rate limit, …).
 */
export async function assertRepoAccess(
  token: string,
  slug: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GithubAccess> {
  const org = await probeOrg(token, orgOf(slug), slug, fetchImpl);

  const repo = await fetchImpl(`https://api.github.com/repos/${slug}`, {
    headers: headersFor(token),
  });
  if (repo.ok) {
    return {
      target: slug,
      canPush: await boolField(repo, b => b?.permissions?.push),
      membersCanCreateRepos: org.membersCanCreateRepos,
    };
  }
  const trace = [...org.trace, step(`repos/${slug}`, repo)];
  assertCredentials(repo, slug);
  if (repo.status === 404 || repo.status === 403) {
    // Org readable but not the repo. GitHub answers a repository lookup from
    // an unauthorized token with a bare 404 and no SSO header, while still
    // serving the organization's own metadata with 200 — so the org probe
    // cannot be relied on to reveal it, and the token's scopes are the only
    // evidence that separates the two causes:
    //
    //  - holds `repo` → the organization has not authorized this token (SAML
    //    SSO, or an OAuth App access policy), or the repo isn't visible to
    //    this user. Signing out would not help, so don't call it `scope`.
    //  - lacks `repo`, or GitHub didn't say → treat as scope, which a fresh
    //    authorization fixes (along with a merely stale token).
    const url = ssoAuthorizeUrl(repo);
    if (url || !org.res.ok) {
      throw new RepoAccessError(withTrace(ssoHint(slug, Boolean(url)), trace), {
        kind: 'sso',
        authorizeUrl: url,
      });
    }
    // A GitHub App token has no scopes to be short of, and re-authorizing
    // mints an equally-limited one — so this is the installation's reach.
    if (isAppUserToken(token)) {
      throw new RepoAccessError(withTrace(installHint(slug), trace), {
        kind: 'install',
      });
    }
    if (hasRepoScope(org.res) === true) {
      throw new RepoAccessError(withTrace(orgAuthHint(slug), trace), {
        kind: 'org-auth',
        authorizeUrl: orgSsoUrl(orgOf(slug)),
      });
    }
    throw new RepoAccessError(withTrace(scopeHint(slug), trace), {
      kind: 'scope',
    });
  }
  throw new Error(`GitHub returned ${repo.status} for ${slug}`);
}

/**
 * Prove `token` is authorized for `org` itself, without naming a repository
 * in it. Used where the run will create a repo rather than touch an existing
 * one, so there is nothing yet to look up — SSO authorization is granted per
 * organization and per token, so each org the run touches needs its own probe.
 */
export async function assertOrgAccess(
  token: string,
  org: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GithubAccess> {
  const probe = await probeOrg(token, org, org, fetchImpl);
  if (!probe.res.ok) {
    // 404/403 with no SSO header: the org is invisible to this token. Offer
    // the same single sign-on route as an unauthorized repo — it is the best
    // next action, and the hint covers the "not a member" case too.
    if (isAppUserToken(token)) {
      throw new RepoAccessError(withTrace(installHint(org), probe.trace), {
        kind: 'install',
      });
    }
    throw new RepoAccessError(withTrace(orgAuthHint(org), probe.trace), {
      kind: 'org-auth',
      authorizeUrl: orgSsoUrl(org),
    });
  }
  return { target: org, membersCanCreateRepos: probe.membersCanCreateRepos };
}

/** Probe a repository (`owner/name`) or a bare organization (`owner`). */
function assertAccess(
  token: string,
  target: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GithubAccess> {
  return target.includes('/')
    ? assertRepoAccess(token, target, fetchImpl)
    : assertOrgAccess(token, target, fetchImpl);
}

export interface GithubTokenSource {
  /** Token holding at least `scopes`; prompts the user when there is no session. */
  getAccessToken(scopes: string[]): Promise<string>;
  /** Drop the GitHub session only (the Backstage sign-in is untouched). */
  signOut(): Promise<void>;
}

/**
 * Drop `targets` that are already covered: duplicates, and a bare organization
 * probe when some repository in that same organization is being probed anyway
 * (that probe checks the org first). Order is preserved, because probing in the
 * order the run will use them means the first problem reported is the first one
 * the user has to fix.
 */
export function probeTargets(targets: string[]): string[] {
  const orgsCovered = new Set(
    targets
      .filter(t => t.includes('/'))
      .map(orgOf)
      .filter(Boolean),
  );
  return targets.filter(
    (t, i) =>
      Boolean(t) &&
      targets.indexOf(t) === i &&
      (t.includes('/') || !orgsCovered.has(t)),
  );
}

async function probeAll(
  token: string,
  targets: string[],
  fetchImpl: typeof fetch,
): Promise<GithubAccess[]> {
  const access: GithubAccess[] = [];
  for (const target of targets) {
    access.push(await assertAccess(token, target, fetchImpl));
  }
  return access;
}

/**
 * A GitHub token that is authorized for every one of `targets` — each
 * repository the run will touch, and each organization it will create one in.
 *
 * A `scope` problem is fixed here by dropping the GitHub session and
 * authorizing afresh, which requests the scopes explicitly. An `sso` problem
 * cannot be fixed by Backstage — the error carries GitHub's authorization URL
 * for the caller to offer — so it is thrown as is.
 */
export async function tokenWithRepoAccess(
  auth: GithubTokenSource,
  targets: string | string[],
  scopes: string[] = ['repo'],
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string; access: GithubAccess[] }> {
  const list = probeTargets(typeof targets === 'string' ? [targets] : targets);
  const first = await auth.getAccessToken(scopes);
  try {
    return { token: first, access: await probeAll(first, list, fetchImpl) };
  } catch (e) {
    if (!(e instanceof RepoAccessError) || e.problem.kind !== 'scope') throw e;
  }
  await auth.signOut();
  const second = await auth.getAccessToken(scopes);
  return { token: second, access: await probeAll(second, list, fetchImpl) };
}
